import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Card, Icon, Toast } from '@atoms/index';
import { OrderCard } from '@molecules/index';
import type { OrderStatus } from '@/types';
import type { OrderCardOrder } from '@molecules/OrderCard';
import { orderService } from '@services/orderService';
import { reviewService } from '@services/reviewService';
import { useSocket } from '@/hooks/useSocket';
import { useCartStore } from '@store/cartStore';
import { ConfirmModal, ReviewModal, CancelOrderModal } from '@organisms/index';
import { useSmartReorder } from '@/hooks/useSmartReorder';
import { useAuthStore } from '@store/authStore';

type Segment = 'active' | 'past';

const ACTIVE_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'CREATED',
  'PENDING',
  'SCHEDULED',
  'PREPARING',
  'READY',
  'ON_WAY',
]);

const isActiveStatus = (status: OrderStatus) => ACTIVE_STATUSES.has(status);

type SegmentState = {
  orders: OrderCardOrder[];
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasLoadedOnce: boolean;
};

const initialSegmentState: SegmentState = {
  orders: [],
  total: 0,
  page: 1,
  limit: 10,
  isLoading: false,
  isLoadingMore: false,
  hasLoadedOnce: false,
};

const OrderCardSkeleton = () => (
  <div className="bg-white rounded-2xl shadow-card border border-gray-100/50 overflow-hidden animate-pulse">
    <div className="p-4">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-2 flex-1">
              <div className="h-4 bg-gray-200 rounded w-2/3" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
            <div className="h-6 bg-gray-200 rounded-full w-24" />
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded w-5/6" />
            <div className="h-3 bg-gray-200 rounded w-3/5" />
          </div>
          <div className="flex items-center justify-between">
            <div className="h-4 bg-gray-200 rounded w-24" />
            <div className="h-3 bg-gray-200 rounded w-20" />
          </div>
        </div>
      </div>
    </div>
    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-2">
      <div className="h-9 bg-gray-200 rounded-xl w-28" />
    </div>
  </div>
);

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { isConnected, on, off } = useSocket();
  const clearLocalSession = useAuthStore((s) => s.clearLocalSession);

  if (user?.role === 'RESTAURANT') {
    return <Navigate to="/restaurant-panel" replace />;
  }

  const [segment, setSegment] = useState<Segment>('active');
  const [activeState, setActiveState] = useState<SegmentState>(initialSegmentState);
  const [pastState, setPastState] = useState<SegmentState>(initialSegmentState);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const [reviewTargetOrder, setReviewTargetOrder] = useState<OrderCardOrder | null>(null);
  const [cancelTargetOrder, setCancelTargetOrder] = useState<OrderCardOrder | null>(null);
  const [isCancelSubmitting, setIsCancelSubmitting] = useState(false);

  const requestSeqRef = useRef(0);

  const currentState = segment === 'active' ? activeState : pastState;
  const setCurrentState = segment === 'active' ? setActiveState : setPastState;

  const canLoadMore = useMemo(() => {
    return currentState.orders.length < currentState.total;
  }, [currentState.orders.length, currentState.total]);

  const { startReorder, confirmModal, processingOrderId } = useSmartReorder({
    onToast: (type, message) => {
      setToastType(type);
      setToastMessage(message);
    },
    onAfterSuccess: () => navigate('/checkout'),
  });

  const loadOrders = async (options: { mode: 'initial' | 'more'; targetSegment: Segment }) => {
    const seq = ++requestSeqRef.current;
    const state = options.targetSegment === 'active' ? activeState : pastState;
    const nextPage = options.mode === 'more' ? state.page + 1 : 1;

    const applyLoading = (isLoading: boolean, isLoadingMore: boolean) => {
      const updater = options.targetSegment === 'active' ? setActiveState : setPastState;
      updater((prev) => ({
        ...prev,
        isLoading,
        isLoadingMore,
      }));
    };

    applyLoading(options.mode === 'initial', options.mode === 'more');

    try {
      const result = await orderService.getMyOrders({
        segment: options.targetSegment,
        page: nextPage,
        limit: state.limit,
      });

      if (seq !== requestSeqRef.current) return;

      const updater = options.targetSegment === 'active' ? setActiveState : setPastState;
      updater((prev) => ({
        ...prev,
        orders:
          options.mode === 'more'
            ? [...prev.orders, ...result.orders]
            : result.orders,
        total: result.total,
        page: result.page,
        limit: result.limit,
        isLoading: false,
        isLoadingMore: false,
        hasLoadedOnce: true,
      }));
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return;

      applyLoading(false, false);
      const message = err.response?.data?.error?.message || err.message || 'Siparişler yüklenirken bir hata oluştu.';
      setToastType('error');
      setToastMessage(message);
    }
  };

  useEffect(() => {
    if (segment === 'active') {
      if (!activeState.hasLoadedOnce) {
        loadOrders({ mode: 'initial', targetSegment: 'active' });
      }
      return;
    }

    if (!pastState.hasLoadedOnce) {
      loadOrders({ mode: 'initial', targetSegment: 'past' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment]);

  useEffect(() => {
    if (!isConnected) return;

    const handleOrderStatusChanged = (data: { orderId: string; status: OrderStatus }) => {
      const orderId = data?.orderId;
      const status = data?.status;
      if (!orderId || !status) return;

      const nextSegment: Segment = isActiveStatus(status) ? 'active' : 'past';

      const updateInState = (
        state: SegmentState,
        setState: React.Dispatch<React.SetStateAction<SegmentState>>,
      ) => {
        const existingIdx = state.orders.findIndex((o) => o.id === orderId);
        if (existingIdx === -1) return;

        const updated = { ...state.orders[existingIdx], status };
        const remaining = state.orders.filter((o) => o.id !== orderId);

        if ((segment === 'active' ? 'active' : 'past') === nextSegment) {
          const nextOrders = [...remaining];
          nextOrders.splice(existingIdx, 0, updated);
          setState((prev) => ({ ...prev, orders: nextOrders }));
          return;
        }

        setState((prev) => ({ ...prev, orders: remaining }));
      };

      updateInState(activeState, setActiveState);
      updateInState(pastState, setPastState);

      const moveAcross = (from: Segment, to: Segment) => {
        const fromState = from === 'active' ? activeState : pastState;
        const found = fromState.orders.find((o) => o.id === orderId);
        if (!found) return;

        const updated = { ...found, status };
        if (to === 'active') {
          setActiveState((prev) => ({ ...prev, orders: [updated, ...prev.orders] }));
        } else {
          setPastState((prev) => ({ ...prev, orders: [updated, ...prev.orders] }));
        }
      };

      const isCurrentlyInActive = activeState.orders.some((o) => o.id === orderId);
      const isCurrentlyInPast = pastState.orders.some((o) => o.id === orderId);

      if (nextSegment === 'past' && isCurrentlyInActive) {
        moveAcross('active', 'past');
      } else if (nextSegment === 'active' && isCurrentlyInPast) {
        moveAcross('past', 'active');
      }
    };

    on('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
    on('order_status_changed', handleOrderStatusChanged as any);

    return () => {
      off('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
      off('order_status_changed', handleOrderStatusChanged as any);
    };
  }, [isConnected, on, off, activeState.orders, pastState.orders, segment]);

  const handleRate = (order: OrderCardOrder) => {
    setReviewTargetOrder(order);
  };

  const applyReviewToState = (orderId: string, rating: number) => {
    const apply = (state: SegmentState): SegmentState => {
      const next = state.orders.map((o) => {
        if (o.id !== orderId) return o;
        return {
          ...o,
          isReviewed: true,
          reviewRating: rating,
          review: o.review || null,
        };
      });
      return { ...state, orders: next };
    };
    setActiveState((prev) => apply(prev));
    setPastState((prev) => apply(prev));
  };

  const handleCancelOrder = (order: OrderCardOrder) => {
    setCancelTargetOrder(order);
  };

  const confirmCancelOrder = async (reason?: string) => {
    if (!cancelTargetOrder) return;
    try {
      setIsCancelSubmitting(true);
      await orderService.cancelOrder(cancelTargetOrder.id, reason);
      setActiveState((prev) => ({
        ...prev,
        orders: prev.orders.filter((o) => o.id !== cancelTargetOrder.id),
        total: Math.max(0, prev.total - 1),
      }));
      setCancelTargetOrder(null);
      setToastType('success');
      setToastMessage(
        cancelTargetOrder.paymentMethod === 'CREDIT_CARD'
          ? 'Siparişiniz iptal edildi. İade 5–10 iş günü içinde kartınıza yansıyacaktır.'
          : 'Siparişiniz iptal edildi.'
      );
    } catch (err: any) {
      if (err?.response?.status === 401) {
        clearLocalSession();
        setCancelTargetOrder(null);
        navigate('/login');
        return;
      }
      const message = err.response?.data?.error?.message || err.message || 'Sipariş iptal edilemedi.';
      setToastType('error');
      setToastMessage(message);
    } finally {
      setIsCancelSubmitting(false);
    }
  };

  const submitReview = async (payload: { rating: number; comment?: string }) => {
    if (!reviewTargetOrder) return;

    try {
      setIsReviewSubmitting(true);
      const created = await reviewService.createReview({
        orderId: reviewTargetOrder.id,
        rating: payload.rating,
        comment: payload.comment,
      });

      applyReviewToState(reviewTargetOrder.id, created.rating);
      setReviewTargetOrder(null);
      setToastType('success');
      setToastMessage('Değerlendirmen için teşekkürler!');
    } catch (err: any) {
      if (err?.response?.status === 401) {
        clearLocalSession();
        setReviewTargetOrder(null);
        navigate('/login');
        return;
      }
      console.error('Review submit failed:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      setToastType('error');
      setToastMessage('Yorum gönderilemedi, lütfen tekrar deneyin.');
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  const title = segment === 'active' ? 'Aktif Siparişler' : 'Geçmiş Siparişler';

  return (
    <MainLayout>
      <Toast
        message={toastMessage || ''}
        type={toastType}
        isVisible={Boolean(toastMessage)}
        onClose={() => setToastMessage(null)}
        duration={5000}
      />

      <ConfirmModal {...confirmModal} />

      <CancelOrderModal
        isOpen={Boolean(cancelTargetOrder)}
        order={cancelTargetOrder}
        isSubmitting={isCancelSubmitting}
        onConfirm={confirmCancelOrder}
        onClose={() => !isCancelSubmitting && setCancelTargetOrder(null)}
      />

      <ReviewModal
        isOpen={Boolean(reviewTargetOrder)}
        restaurantName={reviewTargetOrder?.restaurant?.name || 'Restoran'}
        isSubmitting={isReviewSubmitting}
        onClose={() => setReviewTargetOrder(null)}
        onSubmit={submitReview}
      />

      <div className="max-w-4xl mx-auto py-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">Siparişlerim</h1>
            <p className="text-gray-600">Siparişlerini takip et, geçmiş siparişlerini tekrar ver.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => loadOrders({ mode: 'initial', targetSegment: segment })}>
            Yenile
          </Button>
        </div>

        <Card className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                segment === 'active' ? 'bg-primary text-white shadow-md' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setSegment('active')}
            >
              Aktif Siparişler
            </button>
            <button
              className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                segment === 'past' ? 'bg-primary text-white shadow-md' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setSegment('past')}
            >
              Geçmiş Siparişler
            </button>
          </div>
        </Card>

        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500">
            {currentState.total > 0 ? `${currentState.orders.length}/${currentState.total}` : '0'}
          </p>
        </div>

        {currentState.isLoading ? (
          <div className="space-y-4">
            <OrderCardSkeleton />
            <OrderCardSkeleton />
            <OrderCardSkeleton />
          </div>
        ) : currentState.orders.length === 0 ? (
          <Card className="p-10 text-center bg-gray-50">
            <Icon name="cart" size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-900 font-bold mb-1">Henüz siparişin yok</p>
            <p className="text-gray-600 text-sm mb-6">
              {segment === 'active'
                ? 'Aktif siparişin bulunmuyor. Hemen yeni bir sipariş ver!'
                : 'Geçmiş siparişin bulunmuyor. İlk siparişini vermek ister misin?'}
            </p>
            <Button onClick={() => navigate('/')}>Hemen Sipariş Ver</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {currentState.orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                variant={segment}
                onReorder={startReorder}
                onRate={handleRate}
                onCancel={handleCancelOrder}
                isReorderLoading={Boolean(processingOrderId && processingOrderId === order.id)}
                isCancelLoading={isCancelSubmitting && cancelTargetOrder?.id === order.id}
              />
            ))}

            {canLoadMore && (
              <div className="pt-2">
                <Button
                  variant="secondary"
                  fullWidth
                  disabled={currentState.isLoadingMore}
                  onClick={() => loadOrders({ mode: 'more', targetSegment: segment })}
                >
                  {currentState.isLoadingMore ? 'Yükleniyor...' : 'Daha Fazla Yükle'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MyOrdersPage;

